import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import get from 'lodash/get';
import map from 'lodash/map';
import noop from 'lodash/noop';
import reduce from 'lodash/reduce';

import { useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage } from '@sbv-clients/util-misc';

import {
  Api,
  EmployeeCacheKeys,
  useApi,
  useHoursSummaryFormatGet,
  UserV2ApiApiV2UserEmployeeFilteringSettingsPutRequest,
  UserV2ApiApiV2UserEmployeeGroupingLaborLevelsPutRequest,
  UserV2ApiApiV2UserEmployeeIdFullNameDisplayOrderPutRequest,
  UserV2ApiApiV2UserEmployeeInfoTextPutRequest,
  UserV2ApiApiV2UserEmployeeTimeCardSettingsPutRequest,
} from '../index';
import { UserCacheKeys } from './util/cache-keys';

export const useEmployeeGroupingLaborLevelGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [UserCacheKeys.EMPLOYEES_GROUPING_LABOR_LEVELS],
    queryFn: () =>
      api.userV2Api
        .apiV2UserEmployeeGroupingLaborLevelsGet()
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useUpdateEmployeeGroupingLaborLevels = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    (body: UserV2ApiApiV2UserEmployeeGroupingLaborLevelsPutRequest) => {
      return api.userV2Api.apiV2UserEmployeeGroupingLaborLevelsPut(body);
    },
    {
      onSuccess: async () => {
        queryClient
          .invalidateQueries([UserCacheKeys.EMPLOYEES_GROUPING_LABOR_LEVELS])
          .catch(noop);
        queryClient
          .invalidateQueries([UserCacheKeys.USER_PREFERENCES])
          .catch(noop);
      },
    }
  );
};

const userPreferencesGet = (api: Api) => async () => {
  const response = await api.userV2Api.apiV2UserPreferencesGet();
  const userPreferences = map(response.data, (item) => ({
    preferenceID: item.preferenceID as string,
    preferenceValue: item.preferenceValue as string,
  }));

  const userPreferencesMap = reduce(
    userPreferences,
    (acc: Record<string, string>, item) => {
      acc[item.preferenceID] = item.preferenceValue;
      return acc;
    },
    {}
  );
  return {
    userPreferences,
    userPreferencesMap,
  };
};

export const useUserPreferencesGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [UserCacheKeys.USER_PREFERENCES],
    queryFn: userPreferencesGet(api),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useUserPreferenceValue = (key: string) => {
  const { data: userPreferencesData, isLoading } = useUserPreferencesGet();
  return {
    value: get(userPreferencesData, ['userPreferencesMap', key], ''),
    isLoading,
  };
};

const employeeIdFullNameDisplayOrder = (api: Api) => async () => {
  const response =
    await api.userV2Api.apiV2UserEmployeeIdFullNameDisplayOrderGet();
  return response.data as number;
};

export const useEmployeeIdFullNameDisplayOrder = () => {
  const api = useApi();
  return useQuery({
    queryKey: [UserCacheKeys.FULL_NAME_DISPLAY_ORDER],
    queryFn: employeeIdFullNameDisplayOrder(api),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useUpdateEmployeeIdFullNameDisplayOrder = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: UserV2ApiApiV2UserEmployeeIdFullNameDisplayOrderPutRequest) => {
      return api.userV2Api.apiV2UserEmployeeIdFullNameDisplayOrderPut(body);
    },
    {
      onSuccess: async () => {
        queryClient
          .invalidateQueries([EmployeeCacheKeys.EMPLOYEES])
          .catch(noop);
        queryClient
          .invalidateQueries([UserCacheKeys.USER_PREFERENCES])
          .catch(noop);
        queryClient
          .invalidateQueries([UserCacheKeys.FULL_NAME_DISPLAY_ORDER])
          .catch(noop);
      },
    }
  );
};

const employeeInfoText = (api: Api) => async () => {
  const response = await api.userV2Api.apiV2UserEmployeeInfoTextGet();
  return response?.data;
};

export const useEmployeeInfoText = () => {
  const api = useApi();
  return useQuery({
    queryKey: [UserCacheKeys.EMPLOYEE_INFO_TEXT],
    queryFn: employeeInfoText(api),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useUpdateEmployeeInfoText = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (body: UserV2ApiApiV2UserEmployeeInfoTextPutRequest) => {
      return api.userV2Api.apiV2UserEmployeeInfoTextPut(body);
    },
    {
      onSuccess: async () => {
        queryClient
          .invalidateQueries([UserCacheKeys.EMPLOYEE_INFO_TEXT])
          .catch(noop);
      },
    }
  );
};

const userEmployeeTimeCardSettingsGet = (api: Api) => async () => {
  const res = await api.userV2Api.apiV2UserEmployeeTimeCardSettingsGet();
  const response = {
    ...res.data,
    punchDateIsNotPayDateForeColorInfo: {
      red: res.data.punchDateIsNotPayDateForeColor?.red || 0,
      green: res.data.punchDateIsNotPayDateForeColor?.green || 0,
      blue: res.data.punchDateIsNotPayDateForeColor?.blue || 0,
      alpha: 1,
    },
    totalsBackColorInfo: {
      red: res.data.totalsBackColor?.red || 0,
      green: res.data.totalsBackColor?.green || 0,
      blue: res.data.totalsBackColor?.blue || 0,
      alpha: 1,
    },
  };
  delete response.punchDateIsNotPayDateForeColor;
  delete response.totalsBackColor;
  return response;
};

export const useUserEmployeeTimeCardSettingsGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [UserCacheKeys.EMPLOYEE_TIME_CARD_SETTINGS],
    queryFn: userEmployeeTimeCardSettingsGet(api),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export type UserEmployeeTimeCardSettings = NonNullable<
  ReturnType<typeof useUserEmployeeTimeCardSettingsGet>['data']
>;

const updateUserEmployeeTimeCardSettingsPut =
  (api: Api) =>
  (body: UserV2ApiApiV2UserEmployeeTimeCardSettingsPutRequest) => {
    return api.userV2Api.apiV2UserEmployeeTimeCardSettingsPut(body);
  };

export const useUpdateUserEmployeeTimeCardSettingsPut = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const queryClient = useQueryClient();
  return useMutation(updateUserEmployeeTimeCardSettingsPut(api), {
    onSuccess: () => {
      queryClient
        .invalidateQueries([UserCacheKeys.EMPLOYEE_TIME_CARD_SETTINGS])
        .catch(noop);
      queryClient
        .invalidateQueries([UserCacheKeys.USER_PREFERENCES])
        .catch(noop);
    },
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};

export const useRecentlyViewedEmployeesGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [UserCacheKeys.RECENTLY_VIEWED_EMPLOYEES],
    queryFn: () =>
      api.userV2Api
        .apiV2UserRecentlyViewedEmployeesGet()
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useTimeCardPreferencesContext = () => {
  const { data } = useUserEmployeeTimeCardSettingsGet();
  const { isLoading } = useHoursSummaryFormatGet();
  const { value: hourSummaryFormat } = useUserPreferenceValue(
    'HoursSummaryFormatForPayDate'
  );
  const { value: employeeGroupHeaderCount } = useUserPreferenceValue(
    'EmployeeGroupHeaderCount'
  );
  const { value: employeeIDFullNameDisplayOrder } = useUserPreferenceValue(
    'EmployeeIDFullNameDisplayOrder'
  );

  return useMemo(
    () => ({
      isLoading,
      hourSummaryFormat,
      employeeGroupHeaderCount: employeeGroupHeaderCount === 'True',
      employeeIDFullNameDisplayOrder,
      displayActualDateInDailySummarySheet:
        data?.displayActualDateInDailySummarySheet ?? false,
      displayCommentColumnInTimeCards:
        data?.displayCommentColumnInTimeCards ?? false,
      displayDepartmentColumnInTimeCards:
        data?.displayDepartmentColumnInTimeCards ?? false,
      displayDepartmentColumnInTotals:
        data?.displayDepartmentColumnInTotals ?? false,
      displayPayTypeDescription: data?.displayPayTypeDescription ?? false,
      displayRunningTotalColumn: data?.displayRunningTotalColumn ?? false,
      showWorkedHoursOnly: data?.showWorkedHoursOnly ?? false,
      showZeroMinutes: data?.showZeroMinutes ?? false,
      hideAllPayRatesAndAmountsUnlessCtrlPressed:
        data?.hideAllPayRatesAndAmountsUnlessCtrlPressed ?? false,
      hidePriorDateOnTimeCard: data?.hidePriorDateOnTimeCard ?? false,
      hideFollowingDateOnTimeCard: data?.hideFollowingDateOnTimeCard ?? false,
      displayShiftBreakout: data?.displayShiftBreakout ?? false,
      addToTotalMinutesOnly: data?.addToTotalMinutesOnly ?? false,
      hideCalculationNotesColumnInTimeCards:
        data?.hideCalculationNotesColumnInTimeCards ?? false,
    }),
    [
      isLoading,
      hourSummaryFormat,
      employeeGroupHeaderCount,
      employeeIDFullNameDisplayOrder,
      data?.displayActualDateInDailySummarySheet,
      data?.displayCommentColumnInTimeCards,
      data?.displayDepartmentColumnInTimeCards,
      data?.displayDepartmentColumnInTotals,
      data?.displayPayTypeDescription,
      data?.displayRunningTotalColumn,
      data?.showWorkedHoursOnly,
      data?.showZeroMinutes,
      data?.hideAllPayRatesAndAmountsUnlessCtrlPressed,
      data?.hidePriorDateOnTimeCard,
      data?.hideFollowingDateOnTimeCard,
      data?.displayShiftBreakout,
      data?.addToTotalMinutesOnly,
      data?.hideCalculationNotesColumnInTimeCards,
    ]
  );
};
